import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/hooks/useAuth';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronRight, CalendarClock, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// ── Types ────────────────────────────────────────────────────────────────────

type ScheduleStatus = 'scheduled' | 'departed' | 'arrived' | 'cancelled' | 'delayed';
type TransportMode = 'sea' | 'air' | 'land' | 'rail';

interface ServiceSchedule {
  id: string;
  from_node_id: string;
  to_node_id: string;
  transport_mode: TransportMode;
  vehicle_ref: string;
  departure_at: string;
  arrival_at: string;
  cut_off_at?: string;
  capacity_kg?: number;
  notes?: string;
  status: ScheduleStatus;
}

// ── Labels & Colors ───────────────────────────────────────────────────────────

const STATUS_LABELS: Record<ScheduleStatus, string> = {
  scheduled: 'مجدول',
  departed: 'غادر',
  arrived: 'وصل',
  cancelled: 'ملغي',
  delayed: 'متأخر',
};

const STATUS_BADGE_CLASS: Record<ScheduleStatus, string> = {
  scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
  departed: 'bg-orange-100 text-orange-700 border-orange-200',
  arrived: 'bg-green-100 text-green-700 border-green-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
  delayed: 'bg-yellow-100 text-yellow-700 border-yellow-200',
};

const TRANSPORT_MODE_LABELS: Record<TransportMode, string> = {
  sea: 'بحري',
  air: 'جوي',
  land: 'بري',
  rail: 'سكة حديد',
};

const ALL_STATUSES: ScheduleStatus[] = ['scheduled', 'departed', 'arrived', 'cancelled', 'delayed'];
const ALL_MODES: TransportMode[] = ['sea', 'air', 'land', 'rail'];

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(dt?: string): string {
  if (!dt) return '-';
  try {
    return new Intl.DateTimeFormat('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dt));
  } catch {
    return dt;
  }
}

function defaultForm() {
  return {
    from_node_id: '',
    to_node_id: '',
    transport_mode: '' as TransportMode | '',
    vehicle_ref: '',
    departure_at: '',
    arrival_at: '',
    cut_off_at: '',
    capacity_kg: '',
    notes: '',
  };
}

type FormState = ReturnType<typeof defaultForm>;

// ── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ScheduleStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASS[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

// ── EditStatusDialog ──────────────────────────────────────────────────────────

interface EditStatusDialogProps {
  schedule: ServiceSchedule;
  onClose: () => void;
  onSuccess: () => void;
  carrierId: string;
  patch: (url: string, data?: unknown) => Promise<unknown>;
}

function EditStatusDialog({ schedule, onClose, onSuccess, carrierId, patch }: EditStatusDialogProps) {
  const [status, setStatus] = useState<ScheduleStatus>(schedule.status);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await patch(
        `/api/v1/carriers/${carrierId}/service-schedules/${schedule.id}`,
        { status }
      );
      toast.success('تم تحديث الحالة بنجاح');
      onSuccess();
      onClose();
    } catch {
      toast.error('تعذر تحديث الحالة');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-sm" dir="rtl">
        <DialogHeader>
          <DialogTitle>تعديل حالة الجدول</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">المرجع: {schedule.vehicle_ref}</p>
          <div className="space-y-2">
            <Label>الحالة الجديدة</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as ScheduleStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ALL_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>إلغاء</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'جارٍ الحفظ...' : 'حفظ'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ServiceSchedulesPage() {
  const navigate = useNavigate();
  const { get, post, patch, delete: del, loading } = useApi();
  const { user } = useAuth();

  const [schedules, setSchedules] = useState<ServiceSchedule[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ServiceSchedule | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<FormState>(defaultForm());

  const carrierId = user?.carrier_id ?? '';

  const fetchSchedules = useCallback(async () => {
    if (!carrierId) return;
    try {
      const res: any = await get(`/api/v1/carriers/${carrierId}/service-schedules`);
      setSchedules(res?.list ?? res?.data ?? res ?? []);
    } catch {
      toast.error('تعذر تحميل الجداول الزمنية');
    } finally {
      setHasFetched(true);
    }
  }, [carrierId, get]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleDelete = async (id: string) => {
    if (!carrierId) return;
    try {
      await del(`/api/v1/carriers/${carrierId}/service-schedules/${id}`);
      toast.success('تم حذف الجدول بنجاح');
      fetchSchedules();
    } catch {
      toast.error('تعذر حذف الجدول');
    }
  };

  const handleSubmit = async () => {
    if (!carrierId) return;
    if (!form.from_node_id.trim()) {
      toast.error('معرف العقدة المصدر مطلوب');
      return;
    }
    if (!form.to_node_id.trim()) {
      toast.error('معرف العقدة الوجهة مطلوب');
      return;
    }
    if (!form.transport_mode) {
      toast.error('وسيلة النقل مطلوبة');
      return;
    }
    if (!form.vehicle_ref.trim()) {
      toast.error('مرجع المركبة مطلوب');
      return;
    }
    if (!form.departure_at) {
      toast.error('وقت الإقلاع مطلوب');
      return;
    }
    if (!form.arrival_at) {
      toast.error('وقت الوصول مطلوب');
      return;
    }
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        from_node_id: form.from_node_id.trim(),
        to_node_id: form.to_node_id.trim(),
        transport_mode: form.transport_mode,
        vehicle_ref: form.vehicle_ref.trim(),
        departure_at: form.departure_at,
        arrival_at: form.arrival_at,
        cut_off_at: form.cut_off_at || undefined,
        capacity_kg: form.capacity_kg ? parseFloat(form.capacity_kg) : undefined,
        notes: form.notes.trim() || undefined,
      };
      await post(`/api/v1/carriers/${carrierId}/service-schedules`, payload);
      toast.success('تم إنشاء الجدول بنجاح');
      setShowCreate(false);
      setForm(defaultForm());
      fetchSchedules();
    } catch {
      toast.error('تعذر إنشاء الجدول');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <PageContainer>
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ChevronRight className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold">الجداول الزمنية</h1>
                <p className="text-sm text-muted-foreground">إدارة جداول الرحلات والإبحار والشحن الجوي</p>
              </div>
            </div>
            <Button onClick={() => setShowCreate(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              إضافة جدولة
            </Button>
          </div>
        </div>

        {/* Content */}
        {(!hasFetched || loading) ? (
          <LoadingSkeleton type="card" rows={4} />
        ) : schedules.length === 0 ? (
          <EmptyState
            icon={<CalendarClock className="h-16 w-16" />}
            title="لا توجد جداول زمنية"
            description="أضف جدولاً زمنياً لتتبع مواعيد الرحلات والإبحار"
            action={{ label: 'إضافة جدولة', onClick: () => setShowCreate(true) }}
          />
        ) : (
          <div className="space-y-3">
            {schedules.map((schedule) => (
              <Card key={schedule.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0 space-y-2">
                      {/* Top row */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium" dir="ltr">{schedule.vehicle_ref}</span>
                        <Badge variant="outline" className="text-xs">
                          {TRANSPORT_MODE_LABELS[schedule.transport_mode]}
                        </Badge>
                        <StatusBadge status={schedule.status} />
                      </div>

                      {/* Route */}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                        <span dir="ltr" className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                          {schedule.from_node_id.slice(0, 8)}…
                        </span>
                        <span>→</span>
                        <span dir="ltr" className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                          {schedule.to_node_id.slice(0, 8)}…
                        </span>
                      </div>

                      {/* Times */}
                      <div className="flex gap-4 text-sm flex-wrap">
                        <span>
                          <span className="text-muted-foreground">الإقلاع: </span>
                          {formatDateTime(schedule.departure_at)}
                        </span>
                        <span>
                          <span className="text-muted-foreground">الوصول: </span>
                          {formatDateTime(schedule.arrival_at)}
                        </span>
                      </div>

                      {schedule.capacity_kg != null && (
                        <div className="text-xs text-muted-foreground">
                          السعة: {schedule.capacity_kg} كجم
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setEditingSchedule(schedule)}
                        title="تعديل الحالة"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(schedule.id)}
                        title="حذف"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={(open) => {
          if (!open) setForm(defaultForm());
          setShowCreate(open);
        }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
            <DialogHeader>
              <DialogTitle>إضافة جدولة زمنية جديدة</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* From / To Node IDs */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>
                    من (UUID العقدة) <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    placeholder="UUID"
                    value={form.from_node_id}
                    onChange={(e) => setField('from_node_id', e.target.value)}
                    dir="ltr"
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    إلى (UUID العقدة) <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    placeholder="UUID"
                    value={form.to_node_id}
                    onChange={(e) => setField('to_node_id', e.target.value)}
                    dir="ltr"
                  />
                </div>
              </div>

              {/* Transport Mode */}
              <div className="space-y-2">
                <Label>
                  وسيلة النقل <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={form.transport_mode}
                  onValueChange={(v) => setField('transport_mode', v as TransportMode)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="اختر وسيلة النقل" />
                  </SelectTrigger>
                  <SelectContent>
                    {ALL_MODES.map((m) => (
                      <SelectItem key={m} value={m}>{TRANSPORT_MODE_LABELS[m]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Vehicle Ref */}
              <div className="space-y-2">
                <Label>
                  المرجع (رقم الرحلة / السفينة) <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="مثال: SA123 أو VESSEL-001"
                  value={form.vehicle_ref}
                  onChange={(e) => setField('vehicle_ref', e.target.value)}
                  dir="ltr"
                />
              </div>

              {/* Departure & Arrival */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>
                    وقت الإقلاع <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    type="datetime-local"
                    value={form.departure_at}
                    onChange={(e) => setField('departure_at', e.target.value)}
                    dir="ltr"
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    وقت الوصول <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    type="datetime-local"
                    value={form.arrival_at}
                    onChange={(e) => setField('arrival_at', e.target.value)}
                    dir="ltr"
                  />
                </div>
              </div>

              {/* Cut-off */}
              <div className="space-y-2">
                <Label>آخر موعد للاستلام (اختياري)</Label>
                <Input
                  type="datetime-local"
                  value={form.cut_off_at}
                  onChange={(e) => setField('cut_off_at', e.target.value)}
                  dir="ltr"
                />
              </div>

              {/* Capacity */}
              <div className="space-y-2">
                <Label>السعة (كجم) — اختياري</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="اختياري"
                  value={form.capacity_kg}
                  onChange={(e) => setField('capacity_kg', e.target.value)}
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>ملاحظات</Label>
                <Textarea
                  placeholder="اختياري"
                  value={form.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreate(false);
                  setForm(defaultForm());
                }}
                disabled={submitting}
              >
                إلغاء
              </Button>
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'جارٍ الحفظ...' : 'حفظ الجدول'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Status Dialog */}
        {editingSchedule && carrierId && (
          <EditStatusDialog
            schedule={editingSchedule}
            carrierId={carrierId}
            patch={patch}
            onClose={() => setEditingSchedule(null)}
            onSuccess={fetchSchedules}
          />
        )}
      </PageContainer>
    </div>
  );
}
